
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation } from 'vuex-class'
import { TranslationHandler } from './mixins/TranslationHandler'
import { FontMixin, Font } from 'umg-components/src/mixins/Font'
import AppFooter from './components/AppFooter.vue'
import AppHeader from './components/AppHeader.vue'
import AssetLoading from './components/AssetLoading.vue'
import FaqModal from './components/FaqModal.vue'
import VideoModal from './components/VideoModal.vue'
import { EventBus } from '../event-bus'
import { ON_PARTY_CHANGE } from '../constants/events'
import { cms } from '../cms'

@Component({
  components: {
    AppHeader, AppFooter, AssetLoading, FaqModal, VideoModal
  },
})
export default class App extends Mixins(TranslationHandler, FontMixin) {
  @Action('SOCKET_SETUP') setupSocket!: () => void
  @Action('MOBILE_DETECT') mobileDetect!: () => void

  @Action('CHAT_ON_INCOMING_MESSAGES') chatOnIncomingMessages!: () => void
  @Action('CHAT_ON_INCOMING_POPUP') chatOnIncomingPopup!: () => void

  @Action('USER_LOGIN') userLogin!: (p: { service: string, artistIds: string[] }) => Promise<void>
  @Action('USER_LOAD_ALL') loadAllUsers!: () => Promise<void>
  @Getter userIsAuthenticated!: boolean
  @Getter userName!: string
  @Getter userEmail!: string

  @Getter isAdmin!: boolean;

  @Getter visualIsDark!: boolean
  @Getter visualPrimaryColor!: boolean
  @Getter visualBackgroundColor!: boolean
  @Getter visualFont!: Font
  @Getter visualTextColor!: string

  @Getter userIsAdmin!: boolean
  @Getter userIsHost!: boolean

  @Action('PARTY_STATUS') playerStatus!: () => Promise<void>
  @Action('PARTY_GET_BY_ID') partyGet!: () => Promise<void>
  @Action('PLAYER_INIT') playerInit!: () => Promise<void>
  @Action('INIT_APP') initApp!: () => Promise<void>
  @Action('USER_INIT') userInit!: () => Promise<void>
  @Mutation('PARTY_SET_ENDS_AT') partySetEndAt!: (date: Date) => void
  @Mutation('PARTY_SET_STARTS_AT') partySetStartAt!: (date: Date) => void
  @Mutation('PARTY_SET_IS_VOTE') partySetIsVote!: (v: boolean) => void
  @Getter partyDateIsStarted!: boolean
  @Getter partyDateIsEnded!: () => boolean
  @Getter partyServices!: string[]

  @Getter isFaqOpen!: boolean
  @Mutation('SET_IS_FAQ_OPEN') setFaqOpen!: (isOpen: boolean) => void

  @Getter isVideoOpen!: boolean
  @Mutation('SET_IS_VIDEO_OPEN') setVideoOpen!: (isOpen: boolean) => void

  loaded: boolean = false

  localeKey: string = ''

  async created() {
    cms.newsletter.initBanner()
    // Load cms, campaign and sdks
    await this.initApp()
    // Load token and current user if authenticated
    await this.userInit()
    // Remove query params from url
    this.routerReset()
    this.mobileDetect()
    window.addEventListener('resize', () => {
      this.mobileDetect()
    })

    await this.playerStatus()
    try {
      if (this.userIsHost || this.userIsAdmin) {
        this.loadAllUsers()
      }
    } catch (e) {
      console.error(e)
    }
    if (this.userIsAuthenticated) {
      cms.tracking.setTrackingUser({ logged_in: true, email: this.userEmail })
    } else {
      cms.tracking.setTrackingUser(null)
    }
    cms.tracking.init()
    this.$router.afterEach(() => {
      cms.tracking.trackRouteChange()
    })

    this.routeHandler()
    await this.playerInit()
    this.loaded = true
    this.$emit('loaded')

    this.insertFont()
  }

  mounted() {
    EventBus.$on(ON_PARTY_CHANGE, async (event: { voting_enabled: boolean, ends_at: string, starts_at: string }) => {
      this.partySetStartAt(new Date(event.starts_at))
      this.partySetEndAt(new Date(event.ends_at))
      this.partySetIsVote(event.voting_enabled)
      // if party ended, redirect users to end page
      this.routeHandler()
    })
  }

  @Watch('$route')
  watchRoute() {
    if (this.loaded === false) { return }

    this.routeHandler()
  }

  @Watch('userIsAuthenticated')
  watchToken(value: boolean) {
    if (!value) return

    this.setupSocket()
    this.chatOnIncomingMessages()
    this.chatOnIncomingPopup()
  }

  setLocale(locale: string) {
    this.$setLocale(locale)
    this.localeKey = locale
  }

  insertFont() {
    const style = document.createElement('link')
    style.rel = 'stylesheet'
    style.href = this.fontLink

    document.head.appendChild(style)
  }

  get fontLink(): string {
    switch (this.visualFont) {
      case 'mono': {
        return 'https://fonts-googleapis-com.universal-music.de/css?family=Fira+Sans:400,500,800'
      }
      case 'poppins': {
        return 'https://fonts-googleapis-com.universal-music.de/css?family=Poppins:400,600,700&display=swap'
      }
      case 'playfair': {
        return 'https://fonts-googleapis-com.universal-music.de/css2?family=Playfair+Display:wght@400;500;800&display=swap'
      }
      case 'merriweather': {
        return 'https://fonts-googleapis-com.universal-music.de/css2?family=Merriweather:wght@300;400;900&display=swap'
      }
      case 'rubik': {
        return 'https://fonts-googleapis-com.universal-music.de/css2?family=Rubik:wght@300;400;500;700&display=swap'
      }
      default: {
        return ''
      }
    }
  }

  routerReset() {
    if (this.isAdmin) { return }

    history.replaceState(
      '', document.title, window.location.origin + window.location.pathname + window.location.hash
    )
  }

  routeHandler() {
    let routeName

    if (this.partyDateIsStarted === false) {
      routeName = 'Pre'
    }
    if (this.partyDateIsEnded() === true) {
      routeName = 'End'
    }

    if (this.isAdmin === false && routeName && routeName !== this.$route.name) {
      this.$router.push({ name: routeName })
    }
  }

  get appStyle() {
    return {
      background: this.visualBackgroundColor,
      color: this.visualTextColor,
      fontFamily: this.$getFontFamily(this.visualFont),
      minHeight: '100vh',
    }
  }

  get isFooterShown() {
    return !['Player'].includes((this.$route.name || '').toString())
  }

  get isHeaderLogoShown() {
    return ['Player'].includes((this.$route.name || '').toString())
  }
}
